import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { FolderOpen } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { MetricSkeleton } from '@/components/dashboard/MetricSkeleton';
import { StatCard } from '@/components/dashboard/StatCard';
import { RiskBar } from '@/components/dashboard/RiskBar';
import { SeverityChip } from '@/components/dashboard/SeverityChip';
import { WidgetCardHeader } from '@/components/dashboard/WidgetCardHeader';
import {
  TIME_PERIOD_30D,
  TIME_PERIOD_90D,
  type TimePeriod,
} from '@/components/dashboard/timePeriod';
import { useAuthStore } from '@/store/authStore';
import { supabase } from '@/services/db/supabase';
import {
  getTenantOperationsSnapshot,
  getPortfolioRiskDistribution,
  getSafetyEventsAnalysis,
  getPositiveObservationsSummary,
  getSWAEventsAnalysis,
  getProjectBreakdown,
  type RiskDistribution,
  type SafetyEventsAnalysis,
  type SWASeverity,
} from '@/services/db/dashboard';
import { getProjectIdsForTenant } from '@/services/db/analytics';
import { getSSEByProject } from '@/services/db/pob';

const STALE = 5 * 60 * 1000;

// ── Hooks ───────────────────────────────────────────────────

function useTenantName() {
  const tenantId = useAuthStore((s) => s.tenantId);
  return useQuery({
    queryKey: ['tenant_name', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('name')
        .eq('id', tenantId!)
        .single();
      if (error) throw error;
      return data?.name ?? 'Your Organization';
    },
    enabled: !!tenantId,
    staleTime: STALE,
  });
}

function useScopeProjectIds() {
  const tenantId = useAuthStore((s) => s.tenantId);
  return useQuery({
    queryKey: ['exec_scope_project_ids', tenantId],
    queryFn: () => getProjectIdsForTenant(tenantId!),
    enabled: !!tenantId,
    staleTime: STALE,
  });
}

function useOperationsSnapshot() {
  const tenantId = useAuthStore((s) => s.tenantId);
  return useQuery({
    queryKey: ['exec_ops_snapshot', tenantId],
    queryFn: () => getTenantOperationsSnapshot(tenantId!),
    enabled: !!tenantId,
    staleTime: STALE,
  });
}

function useSafetyEvents(projectIds: string[], enabled: boolean, period: TimePeriod) {
  return useQuery({
    queryKey: ['exec_safety_events', projectIds, period.days],
    queryFn: () => getSafetyEventsAnalysis(projectIds, period.days),
    enabled,
    staleTime: STALE,
  });
}

function usePortfolioRisk(projectIds: string[], enabled: boolean, period: TimePeriod) {
  return useQuery({
    queryKey: ['exec_portfolio_risk', projectIds, period.days],
    queryFn: () => getPortfolioRiskDistribution(projectIds, period.days),
    enabled,
    staleTime: STALE,
  });
}

function usePositiveObs(projectIds: string[], enabled: boolean, period: TimePeriod) {
  return useQuery({
    queryKey: ['exec_positive_obs', projectIds, period.days],
    queryFn: () => getPositiveObservationsSummary(projectIds, period.days),
    enabled,
    staleTime: STALE,
  });
}

function useSWAEvents(projectIds: string[], enabled: boolean, period: TimePeriod) {
  return useQuery({
    queryKey: ['exec_swa_events', projectIds, period.days],
    queryFn: () => getSWAEventsAnalysis(projectIds, period.days),
    enabled,
    staleTime: STALE,
  });
}

function useWorkforceSSE(projectIds: string[], enabled: boolean) {
  return useQuery({
    queryKey: ['exec_workforce_sse', projectIds],
    queryFn: () => getSSEByProject(projectIds),
    enabled,
    staleTime: STALE,
  });
}

function useProjectHealth() {
  const tenantId = useAuthStore((s) => s.tenantId);
  return useQuery({
    queryKey: ['exec_project_health', tenantId],
    queryFn: () => getProjectBreakdown(tenantId!),
    enabled: !!tenantId,
    staleTime: STALE,
  });
}

// ── Helpers ─────────────────────────────────────────────────

function trendIndicator(trend: 'up' | 'down' | 'flat'): { symbol: string; color: string } {
  switch (trend) {
    case 'up':
      return { symbol: '↑', color: 'text-mozaic-red' };
    case 'down':
      return { symbol: '↓', color: 'text-mozaic-green' };
    case 'flat':
      return { symbol: '→', color: 'text-muted-foreground' };
  }
}

function formatCategory(raw: string): string {
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function projectHealthBorder(safetyEvents30d: number, hasRecentDSR: boolean): string {
  if (!hasRecentDSR || safetyEvents30d >= 3) return 'border-l-4 border-l-mozaic-red';
  if (safetyEvents30d >= 1) return 'border-l-4 border-l-mozaic-orange';
  return 'border-l-4 border-l-mozaic-green';
}

const RISK_LEVEL_STYLE: Record<string, string> = {
  critical: 'bg-mozaic-red/15 text-mozaic-red',
  high: 'bg-mozaic-red/15 text-mozaic-red',
  medium: 'bg-mozaic-orange/15 text-mozaic-orange',
  low: 'bg-mozaic-green/15 text-mozaic-green',
};

function formatRiskLevel(v: string | null): string {
  if (!v) return 'Not Assessed';
  return v.charAt(0).toUpperCase() + v.slice(1);
}

// ── Widget: Operations Status ───────────────────────────────

function OperationsStatusCard() {
  const snapshot = useOperationsSnapshot();
  return (
    <Card>
      <WidgetCardHeader
        title="Operations Status"
        lastUpdated={snapshot.dataUpdatedAt}
      />
      <CardContent>
        {snapshot.isLoading ? (
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-1">
                <Skeleton className="h-7 w-12" />
                <Skeleton className="h-3 w-24" />
              </div>
            ))}
          </div>
        ) : snapshot.isError || !snapshot.data ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <StatCard label="Active Projects" value={snapshot.data.activeProjects} />
            <StatCard label="Active Locations" value={snapshot.data.activeLocations} />
            <StatCard label="SSRs on Assignment" value={snapshot.data.ssrsOnAssignment} />
            <StatCard label="DSRs Submitted Today" value={snapshot.data.dsrsSubmittedToday} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Widget: Safety Events L90D ──────────────────────────────

function SafetyEventsCard({
  query,
  period,
  onPeriodChange,
  onClickTotal,
}: {
  query: ReturnType<typeof useSafetyEvents>;
  period: TimePeriod;
  onPeriodChange: (p: TimePeriod) => void;
  onClickTotal: () => void;
}) {
  return (
    <Card>
      <WidgetCardHeader
        title="Safety Events"
        period={period}
        onPeriodChange={onPeriodChange}
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent>
        {query.isLoading ? (
          <MetricSkeleton />
        ) : query.isError || !query.data ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : (
          <SafetyEventsContent
            data={query.data}
            period={period}
            onClickTotal={onClickTotal}
          />
        )}
      </CardContent>
    </Card>
  );
}

function SafetyEventsContent({
  data,
  period,
  onClickTotal,
}: {
  data: SafetyEventsAnalysis;
  period: TimePeriod;
  onClickTotal: () => void;
}) {
  const trend = trendIndicator(data.trend);
  const lti = data.current.lost_time_injury;
  return (
    <>
      <div className="flex items-baseline gap-2">
        <p
          className={`text-3xl font-bold ${lti > 0 ? 'text-mozaic-red' : 'text-foreground'}`}
        >
          {lti}
        </p>
        <span className="text-sm text-muted-foreground">LTI</span>
        <span className={`ml-auto text-lg font-semibold ${trend.color}`}>
          {trend.symbol}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Near Miss</span>
          <span className="font-medium">{data.current.near_miss}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">First Aid</span>
          <span className="font-medium">{data.current.first_aid}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Recordable</span>
          <span className="font-medium">{data.current.recordable_injury}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Property / Env.</span>
          <span className="font-medium">
            {data.current.property_damage + data.current.environmental_release}
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={onClickTotal}
        className="mt-2 text-left text-xs text-muted-foreground hover:text-mozaic-teal hover:underline"
      >
        {data.current.total} total · vs {data.previous.total} in prior {period.days} days
      </button>
    </>
  );
}

// ── Widget: Portfolio Risk L30D ─────────────────────────────

function PortfolioRiskCard({
  query,
  period,
  onPeriodChange,
  onRiskClick,
}: {
  query: ReturnType<typeof usePortfolioRisk>;
  period: TimePeriod;
  onPeriodChange: (p: TimePeriod) => void;
  onRiskClick: (level: 'high' | 'medium' | 'low' | 'not_assessed') => void;
}) {
  return (
    <Card>
      <WidgetCardHeader
        title="Portfolio Risk"
        period={period}
        onPeriodChange={onPeriodChange}
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent>
        {query.isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : query.isError || !query.data ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : query.data.total === 0 ? (
          <p className="pt-2 text-xs text-muted-foreground">
            No submitted DSRs in the selected period
          </p>
        ) : (
          <PortfolioRiskBars data={query.data} onRiskClick={onRiskClick} />
        )}
      </CardContent>
    </Card>
  );
}

function PortfolioRiskBars({
  data,
  onRiskClick,
}: {
  data: RiskDistribution;
  onRiskClick: (level: 'high' | 'medium' | 'low' | 'not_assessed') => void;
}) {
  return (
    <div className="space-y-2.5">
      <button
        type="button"
        onClick={() => onRiskClick('high')}
        className="block w-full text-left hover:opacity-80"
      >
        <RiskBar label="High" pct={data.highPct} count={data.high} barClass="bg-mozaic-red" />
      </button>
      <button
        type="button"
        onClick={() => onRiskClick('medium')}
        className="block w-full text-left hover:opacity-80"
      >
        <RiskBar label="Medium" pct={data.mediumPct} count={data.medium} barClass="bg-mozaic-orange" />
      </button>
      <button
        type="button"
        onClick={() => onRiskClick('low')}
        className="block w-full text-left hover:opacity-80"
      >
        <RiskBar label="Low" pct={data.lowPct} count={data.low} barClass="bg-mozaic-green" />
      </button>
      {data.notAssessed > 0 && (
        <button
          type="button"
          onClick={() => onRiskClick('not_assessed')}
          className="block w-full text-left hover:opacity-80"
        >
          <RiskBar
            label="Not Assessed"
            pct={data.notAssessedPct}
            count={data.notAssessed}
            barClass="bg-muted-foreground/50"
          />
        </button>
      )}
      <p className="pt-1 text-xs text-muted-foreground">
        {data.total} DSR {data.total === 1 ? 'day' : 'days'} assessed
      </p>
    </div>
  );
}

// ── Widget: Positive Observations L30D ──────────────────────

function PositiveObservationsCard({
  query,
  period,
  onPeriodChange,
}: {
  query: ReturnType<typeof usePositiveObs>;
  period: TimePeriod;
  onPeriodChange: (p: TimePeriod) => void;
}) {
  return (
    <Card>
      <WidgetCardHeader
        title="Positive Observations"
        period={period}
        onPeriodChange={onPeriodChange}
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent>
        {query.isLoading ? (
          <MetricSkeleton />
        ) : query.isError || !query.data ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : query.data.total === 0 ? (
          <>
            <p className="text-3xl font-bold text-muted-foreground">0</p>
            <p className="mt-1 text-xs text-muted-foreground">
              No Good Catch observations in the selected period
            </p>
          </>
        ) : (
          <>
            <div className="flex items-baseline gap-3">
              <p className="text-3xl font-bold text-mozaic-green">
                {query.data.total}
              </p>
              <span className="text-xs text-muted-foreground">
                {query.data.avgPerDSR} avg / DSR
              </span>
            </div>
            {query.data.topCategories.length > 0 && (
              <div className="mt-3 space-y-1">
                <p className="text-xs font-medium text-muted-foreground">
                  Top Categories
                </p>
                <ul className="space-y-0.5 text-xs">
                  {query.data.topCategories.map((c) => (
                    <li key={c.category} className="flex justify-between">
                      <span>{formatCategory(c.category)}</span>
                      <span className="font-medium">{c.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Widget: SWA Events L30D ─────────────────────────────────

const SWA_SEVERITY_ORDER: SWASeverity[] = ['minor', 'moderate', 'serious', 'critical'];

// SWA severity palette matches the mobile SWA form (teal/blue/orange/red)
// for operator-side consistency. Reconciled in S82b — Executive previously
// rendered "minor" as muted, Supervisor previously rendered "minor" as
// green and "serious" as red. Both now align with the mobile palette.
const SWA_SEVERITY_STYLE: Record<SWASeverity, string> = {
  minor: 'bg-mozaic-teal/15 text-mozaic-teal',
  moderate: 'bg-mozaic-blue/15 text-mozaic-blue',
  serious: 'bg-mozaic-orange/15 text-mozaic-orange',
  critical: 'bg-mozaic-red/15 text-mozaic-red',
};

function SWAEventsCard({
  query,
  period,
  onPeriodChange,
  onTotalClick,
}: {
  query: ReturnType<typeof useSWAEvents>;
  period: TimePeriod;
  onPeriodChange: (p: TimePeriod) => void;
  onTotalClick: () => void;
}) {
  return (
    <Card>
      <WidgetCardHeader
        title="SWA Events"
        period={period}
        onPeriodChange={onPeriodChange}
        lastUpdated={query.dataUpdatedAt}
      />
      <CardContent>
        {query.isLoading ? (
          <MetricSkeleton />
        ) : query.isError || !query.data ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : query.data.total === 0 ? (
          <>
            <p className="text-3xl font-bold text-mozaic-green">0</p>
            <p className="mt-1 text-xs text-muted-foreground">
              No Stop Work Authority invocations
            </p>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onTotalClick}
              className="text-3xl font-bold hover:text-mozaic-teal hover:underline"
            >
              {query.data.total}
            </button>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {SWA_SEVERITY_ORDER.map((sev) => {
                const count = query.data!.bySeverity[sev];
                if (count === 0) return null;
                return (
                  <SeverityChip key={sev} className={SWA_SEVERITY_STYLE[sev]}>
                    {formatCategory(sev)} {count}
                  </SeverityChip>
                );
              })}
            </div>
            {query.data.projectsWithCritical.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium text-mozaic-red">
                  Critical events at:
                </p>
                <ul className="mt-1 space-y-0.5 text-xs">
                  {query.data.projectsWithCritical.map((p) => (
                    <li key={p.projectId} className="truncate">
                      {p.projectName}
                      {p.count > 1 && (
                        <span className="ml-1 text-muted-foreground">
                          ({p.count})
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Widget: Workforce (SSE aggregate) ───────────────────────

function WorkforceCard({
  query,
}: {
  query: ReturnType<typeof useWorkforceSSE>;
}) {
  const rows = query.data ?? [];
  const totalPOB = rows.reduce((s, r) => s + r.totalPOB, 0);
  const totalSSE = rows.reduce((s, r) => s + r.sseCount, 0);
  const ssePct =
    totalPOB > 0 ? Math.round((totalSSE / totalPOB) * 1000) / 10 : 0;
  const projectsAbove15 = rows.filter((r) => r.ssePercent > 15).length;
  const projectsWithManifest = rows.filter((r) => r.hasPublishedManifest).length;
  const noManifest = rows.length - projectsWithManifest;

  return (
    <Card>
      <WidgetCardHeader title="Workforce" lastUpdated={query.dataUpdatedAt} />
      <CardContent>
        {query.isLoading ? (
          <MetricSkeleton />
        ) : query.isError ? (
          <p className="text-xs text-muted-foreground">Failed to load</p>
        ) : totalPOB === 0 ? (
          <>
            <p className="text-3xl font-bold text-muted-foreground">—</p>
            <p className="mt-1 text-xs text-muted-foreground">
              No published POB manifests today
              {noManifest > 0 && ` (${noManifest} project${noManifest === 1 ? '' : 's'})`}
            </p>
          </>
        ) : (
          <>
            <div className="flex items-baseline gap-2">
              <p
                className={`text-3xl font-bold ${
                  ssePct > 15 ? 'text-mozaic-orange' : 'text-foreground'
                }`}
              >
                {ssePct}%
              </p>
              <span className="text-xs text-muted-foreground">SSE today</span>
            </div>
            <div className="mt-3 space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total POB</span>
                <span className="font-medium">{totalPOB}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">SSE personnel</span>
                <span className="font-medium">{totalSSE}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Projects &gt; 15% SSE
                </span>
                <span
                  className={`font-medium ${projectsAbove15 > 0 ? 'text-mozaic-orange' : ''}`}
                >
                  {projectsAbove15}
                </span>
              </div>
              {noManifest > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">No manifest today</span>
                  <span className="font-medium">{noManifest}</span>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main ────────────────────────────────────────────────────

export default function ExecutiveDashboard() {
  const navigate = useNavigate();
  const tenantName = useTenantName();
  const scope = useScopeProjectIds();
  const projectIds = scope.data ?? [];
  const scopeReady = !!scope.data;

  // Per-widget time-period state (AC 12.1.10).
  // Executive defaults: Safety Events 90d (per S77 spec), the rest 30d.
  const [safetyPeriod, setSafetyPeriod] = React.useState<TimePeriod>(TIME_PERIOD_90D);
  const [riskPeriod, setRiskPeriod] = React.useState<TimePeriod>(TIME_PERIOD_30D);
  const [positivePeriod, setPositivePeriod] = React.useState<TimePeriod>(TIME_PERIOD_30D);
  const [swaPeriod, setSwaPeriod] = React.useState<TimePeriod>(TIME_PERIOD_30D);

  const safetyEvents = useSafetyEvents(projectIds, scopeReady, safetyPeriod);
  const portfolioRisk = usePortfolioRisk(projectIds, scopeReady, riskPeriod);
  const positiveObs = usePositiveObs(projectIds, scopeReady, positivePeriod);
  const swaEvents = useSWAEvents(projectIds, scopeReady, swaPeriod);
  const workforce = useWorkforceSSE(projectIds, scopeReady);
  const projectHealth = useProjectHealth();

  return (
    <div className="space-y-6">
      {/* Scope context bar */}
      <div className="rounded-md bg-mozaic-navy/5 px-4 py-2 text-sm">
        Viewing:{' '}
        <span className="font-medium">
          {tenantName.data ?? 'Your Organization'}
        </span>{' '}
        — Executive Overview
      </div>

      {/* Row 1: Organization Health Snapshot */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <OperationsStatusCard />
        <SafetyEventsCard
          query={safetyEvents}
          period={safetyPeriod}
          onPeriodChange={setSafetyPeriod}
          onClickTotal={() => navigate('/reports')}
        />
        <PortfolioRiskCard
          query={portfolioRisk}
          period={riskPeriod}
          onPeriodChange={setRiskPeriod}
          onRiskClick={(level) =>
            navigate(level === 'not_assessed' ? '/reports' : `/reports?risk=${level}`)
          }
        />
      </div>

      {/* Row 2: Key Metrics */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <PositiveObservationsCard
          query={positiveObs}
          period={positivePeriod}
          onPeriodChange={setPositivePeriod}
        />
        <SWAEventsCard
          query={swaEvents}
          period={swaPeriod}
          onPeriodChange={setSwaPeriod}
          onTotalClick={() => navigate('/reports')}
        />
        <WorkforceCard query={workforce} />
      </div>

      {/* Row 3: Active Projects */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-muted-foreground">
          Active Projects
        </h2>
        {projectHealth.isLoading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="space-y-2 pt-4">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-24" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : projectHealth.isError ? (
          <p className="text-sm text-muted-foreground">
            Could not load project data.
          </p>
        ) : (projectHealth.data?.length ?? 0) === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
              <FolderOpen className="h-8 w-8 text-muted-foreground/60" />
              <p className="text-sm font-medium">No active projects in this tenant.</p>
              <p className="text-xs text-muted-foreground">
                Active projects appear here once they are created and linked to a location.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {projectHealth.data?.map((project) => {
              const hasRecentDSR = project.lastSubmitted != null;
              const borderClass = projectHealthBorder(
                project.safetyEvents30d,
                hasRecentDSR,
              );
              return (
                <Card
                  key={project.projectId}
                  className={`cursor-pointer hover:bg-muted/50 ${borderClass}`}
                  onClick={() =>
                    navigate(`/reports?project=${project.projectId}`)
                  }
                >
                  <CardContent className="pt-4">
                    <p className="font-semibold">{project.projectName}</p>
                    {(project.businessUnitName || project.divisionName) && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {[project.businessUnitName, project.divisionName]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    )}
                    <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                      <p className="flex justify-between">
                        <span>Location</span>
                        <span className="truncate">
                          {project.currentLocation ?? '—'}
                        </span>
                      </p>
                      <p className="flex justify-between">
                        <span>Last Risk Level</span>
                        {project.lastRiskLevel ? (
                          <Badge
                            className={`capitalize ${
                              RISK_LEVEL_STYLE[project.lastRiskLevel] ?? ''
                            }`}
                          >
                            {formatRiskLevel(project.lastRiskLevel)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">
                            Not Assessed
                          </span>
                        )}
                      </p>
                      <p className="flex justify-between">
                        <span>Safety Events (30d)</span>
                        <span
                          className={
                            project.safetyEvents30d > 0
                              ? 'font-medium text-mozaic-orange'
                              : 'text-mozaic-green'
                          }
                        >
                          {project.safetyEvents30d}
                        </span>
                      </p>
                      <p className="flex justify-between">
                        <span>SWA Events (30d)</span>
                        <span
                          className={
                            project.swaEvents30d > 0
                              ? 'font-medium text-mozaic-orange'
                              : ''
                          }
                        >
                          {project.swaEvents30d}
                        </span>
                      </p>
                      <p className="flex justify-between">
                        <span>Workforce</span>
                        <span>{project.workforce}</span>
                      </p>
                      <p className="flex justify-between">
                        <span>Last DSR</span>
                        <span>
                          {project.lastSubmitted
                            ? formatDistanceToNow(
                                new Date(project.lastSubmitted),
                                { addSuffix: true },
                              )
                            : 'None submitted'}
                        </span>
                      </p>
                      {project.latestStatus && (
                        <p className="flex justify-between pt-1">
                          <span>Today's Status</span>
                          <Badge variant="outline" className="capitalize">
                            {project.latestStatus.replace(/_/g, ' ')}
                          </Badge>
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
